/**
 * 背景虚化（高斯模糊）API
 * 通过 Java 后端转发到 ComfyUI，自动识别前景主体，
 * 保留前景清晰、背景做高斯模糊（类似 Portrait Mode 虚化效果）
 *
 * 流程：
 *   ① 上传原图 → imageUrl      （由调用方上传，复用现有 uploadFile）
 *   ② 提交：POST /web/v1/bgBlur  body: { imageUrl }
 *           ← 返回 { code:200, data:{ prompt_id } }
 *   ③ 轮询：GET  /web/v1/queryBgBlurResult?promptId=xxx
 *           ← code:202 = 进行中，继续轮询
 *           ← code:200 = 完成，data.oss_image_url 即结果图
 *
 * 全部走 apiClient（带 JWT、走 /web 代理、统一响应拦截器）。
 * 注意：query 接口进行中返回 code:202，必须用 allowBizCodes 放行，
 *       否则会被 apiClient 响应拦截器当作错误 reject 并弹错误框。
 */
import apiClient from './config'

var BG_BLUR_SUBMIT_PATH = '/bgBlur'
var BG_BLUR_QUERY_PATH = '/queryBgBlurResult'

/* 轮询参数：3 秒间隔（接口建议 2~3 秒），5 分钟超时 */
var BG_BLUR_POLL_INTERVAL = 2500
var BG_BLUR_POLL_TIMEOUT = 5 * 60 * 1000

/**
 * 提交背景虚化任务
 * @param {Object} params
 *   - imageUrl 原图 URL（先调 uploadFile 上传得到）
 * @returns {Promise<{promptId:string}>}
 */
export function submitBgBlur(params) {
  var imageUrl = params && params.imageUrl
  if (!imageUrl) {
    return Promise.reject(new Error('imageUrl must not be blank'))
  }
  return apiClient.post(BG_BLUR_SUBMIT_PATH, {
    imageUrl: imageUrl
  }, {
    timeout: 60000
  }).then(function(data) {
    /* apiClient 响应拦截器已解包为业务体 { code, data, message } */
    if (data.code !== 200) {
      throw new Error(data.message || '提交背景虚化任务失败')
    }
    var promptId = data.data && data.data.prompt_id
    if (!promptId) {
      throw new Error('未拿到任务ID，请重试')
    }
    return { promptId: promptId }
  })
}

/**
 * 查询背景虚化任务状态（单次）
 * @param {string} promptId
 * @returns {Promise<{code:number, status:string, imageUrl:string, error:string}>}
 *   code: 200 = 完成，202 = 进行中，300 = 参数错误，500 = 任务失败
 */
export function getBgBlurStatus(promptId) {
  return apiClient.get(BG_BLUR_QUERY_PATH, {
    params: { promptId: promptId },
    /* 放行 202 进行中，避免被响应拦截器当错误 reject + 弹框 */
    allowBizCodes: [202],
    timeout: 30000
  }).then(function(data) {
    if (data.code === 200) {
      return {
        code: 200,
        status: 'completed',
        imageUrl: data.data && data.data.oss_image_url
      }
    }
    if (data.code === 202) {
      return { code: 202, status: 'running' }
    }
    return {
      code: data.code,
      status: 'failed',
      error: data.message || '背景虚化失败'
    }
  })
}

/**
 * 轮询背景虚化任务直到完成/失败/超时
 * 用法与 generationMixin 的 pollTask 一致：
 *   pollBgBlur(promptId, function(err, imageUrl) { ... })
 *
 * @param {string}   promptId
 * @param {Function} cb    回调 (error, imageUrl)
 * @returns {{stop:Function}} 停止轮询的句柄
 */
export function pollBgBlur(promptId, cb) {
  var stopped = false
  var startedAt = Date.now()
  var timer = null

  function tick() {
    if (stopped) return
    if (Date.now() - startedAt > BG_BLUR_POLL_TIMEOUT) {
      stopped = true
      cb(new Error('背景虚化任务超时'))
      return
    }

    getBgBlurStatus(promptId).then(function(result) {
      if (stopped) return

      if (result.status === 'completed') {
        stopped = true
        cb(null, result.imageUrl)
        return
      }

      if (result.status === 'failed') {
        stopped = true
        cb(new Error(result.error || '背景虚化失败'))
        return
      }

      /* running → 继续轮询 */
      timer = setTimeout(tick, BG_BLUR_POLL_INTERVAL)
    }).catch(function(err) {
      if (stopped) return
      stopped = true
      cb(err)
    })
  }

  timer = setTimeout(tick, BG_BLUR_POLL_INTERVAL)

  return {
    stop: function() {
      stopped = true
      if (timer) clearTimeout(timer)
    }
  }
}
